/**
 * Platform access orchestrator — merges route/button rules and cross-grants from features.
 * Each feature exports a module: { id, routes, buttons, tryGrantRoute? }.
 */

import { dietAccess } from '../../features/diet/core/access';
import { workoutAccess } from '../../features/workouts/core/access';
import { hrAccess } from '../../features/hr/core/access';
import { hormoneAccess } from '../../features/hormones/core/access';
import { linksAccess } from '../../features/links/core/access';
import { gutAccess } from '../../features/gut/core/access';
import { leadsAccess } from '../../features/leads/core/access';

/** @typedef {Object} AccessRules
 * @property {string[]} departments
 * @property {string[]} roles
 */

/**
 * @typedef {Object} FeatureAccessModule
 * @property {string} id
 * @property {Record<string, AccessRules>} routes
 * @property {Record<string, Record<string, AccessRules>>} [buttons]
 * @property {(route: string, ctx: FeatureRouteGrantContext) => boolean} [tryGrantRoute]
 */

/**
 * @typedef {Object} FeatureRouteGrantContext
 * @property {Array<{ title: string }>} departments
 * @property {Array<{ title: string }>} roles
 * @property {(route: string) => AccessRules|undefined} getRules
 * @property {(rules: AccessRules|undefined, departments: Array, roles: Array) => boolean} passesRules
 */

/** Register feature access modules here (workout, etc. as they are extracted). */
export const FEATURE_ACCESS_MODULES = [dietAccess, workoutAccess, hrAccess, linksAccess, hormoneAccess, leadsAccess, gutAccess];

export function mergeFeatureRoutes(platformRoutes) {
  return FEATURE_ACCESS_MODULES.reduce(
    (merged, module) => ({ ...merged, ...module.routes }),
    { ...platformRoutes }
  );
}

export function mergeFeatureButtons(platformButtons) {
  return FEATURE_ACCESS_MODULES.reduce((merged, module) => {
    if (!module.buttons) return merged;
    const next = { ...merged };
    Object.entries(module.buttons).forEach(([dashboardId, buttons]) => {
      next[dashboardId] = { ...(next[dashboardId] || {}), ...buttons };
    });
    return next;
  }, { ...platformButtons });
}

/**
 * Ask each feature module for a cross-route grant (e.g. diet plan editor from recipes access).
 * @param {string} route
 * @param {FeatureRouteGrantContext} ctx
 * @returns {boolean}
 */
export function tryFeatureRouteGrants(route, ctx) {
  return FEATURE_ACCESS_MODULES.some(
    (module) => module.tryGrantRoute?.(route, ctx) === true
  );
}
